import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { JaPhrase } from '@/i18n/JaPhrase';
import { LanguageSwitcher, legalPath, useI18n } from '@/i18n';
import type { TranslateFn } from '@/i18n/types';
import heroImg from '@/assets/hero-bar.jpg';
import ctaImg from '@/assets/cta-night.jpg';
import pickupImg from '@/assets/pickup-izakaya.jpg';
import phoneHomeImg from '@/assets/phone-home.png';
import phoneMapImg from '@/assets/phone-map.png';
import phoneListImg from '@/assets/phone-list.png';
import phoneMessagesImg from '@/assets/phone-messages.png';
import phoneFeedImg from '@/assets/phone-feed.png';

const BRAND = 'Nhậu Đee';
const CONTACT_EMAIL = 'hello@nhaudee.app';

const OWNER_URL =
  (import.meta.env.VITE_OWNER_PORTAL_URL as string | undefined)?.replace(/\/$/, '') ||
  (import.meta.env.DEV ? 'http://localhost:5173' : 'https://owner.nhaudee.app');

const FEATURE_SCREENS = ['map', 'list', 'messages', 'feed'] as const;
type PhoneVariant = 'home' | 'map' | 'list' | 'messages' | 'feed';

const PHONE_SCREENS: Record<PhoneVariant, string> = {
  home: phoneHomeImg,
  map: phoneMapImg,
  list: phoneListImg,
  messages: phoneMessagesImg,
  feed: phoneFeedImg,
};

function PhoneFrame({ variant, alt }: { variant: PhoneVariant; alt: string }) {
  return (
    <div className={`phone phone-${variant}`}>
      <img className="phone-shot" src={PHONE_SCREENS[variant]} alt={alt} decoding="async" />
    </div>
  );
}

function AppleLogo() {
  return (
    <svg className="store-badge-logo" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"
      />
    </svg>
  );
}

function PlayLogo() {
  return (
    <svg className="store-badge-logo" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M3.6 2.2 13.5 12 3.6 21.8c-.4-.2-.6-.6-.6-1.1V3.3c0-.5.2-.9.6-1.1" />
      <path fill="#FBBC04" d="m13.5 12 2.7-2.7 4.6 2.6c.7.4.7 1.4 0 1.8l-4.6 2.6L13.5 12Z" />
      <path fill="#4285F4" d="M13.5 12 3.6 2.2c.3-.2.7-.2 1.1 0L16.2 9.3 13.5 12Z" />
      <path fill="#34A853" d="M13.5 12 16.2 14.7 4.7 21.8c-.4.2-.8.2-1.1 0L13.5 12Z" />
    </svg>
  );
}

function StoreBadges({ t }: { t: TranslateFn }) {
  return (
    <div className="store-badges">
      <a className="store-badge" href="#download" aria-label={t('store.appStoreAria')}>
        <AppleLogo />
        <span className="store-badge-text">
          <span className="store-badge-kicker">{t('store.appStoreKicker')}</span>
          <span className="store-badge-name">{t('store.appStoreName')}</span>
        </span>
      </a>
      <a className="store-badge" href="#download" aria-label={t('store.playAria')}>
        <PlayLogo />
        <span className="store-badge-text">
          <span className="store-badge-kicker">{t('store.playKicker')}</span>
          <span className="store-badge-name">{t('store.playName')}</span>
        </span>
      </a>
    </div>
  );
}

function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-visible');
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add('is-visible');
          io.disconnect();
        }
      },
      { threshold: 0.14, rootMargin: '0px 0px -6% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Reveal({
  children,
  className = '',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article';
}) {
  const ref = useScrollReveal<HTMLDivElement>();
  return (
    <Tag ref={ref as never} className={`reveal ${className}`.trim()}>
      {children}
    </Tag>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' is-open' : ''}`}>
      <h3>
        <button
          type="button"
          className="faq-trigger"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          <JaPhrase text={q} />
          <span className="faq-icon" aria-hidden>
            {open ? '−' : '+'}
          </span>
        </button>
      </h3>
      <div id={panelId} role="region" className="faq-panel" hidden={!open}>
        <JaPhrase as="p" text={a} />
      </div>
    </div>
  );
}

function Heading({
  as,
  text,
  className = '',
}: {
  as: 'h1' | 'h2' | 'h3';
  text: string;
  className?: string;
}) {
  const { locale } = useI18n();
  if (locale === 'ja') return <JaPhrase as={as} text={text} className={className} />;
  const Tag = as;
  return <Tag className={className}>{text}</Tag>;
}

function Body({ text, className = '' }: { text: string; className?: string }) {
  const { locale } = useI18n();
  if (locale === 'ja') return <JaPhrase as="p" text={text} className={className} />;
  return <p className={className}>{text}</p>;
}

export function LandingPage() {
  const { locale, t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const features = useMemo(
    () =>
      FEATURE_SCREENS.map((screen, i) => {
        const id = String(i + 1).padStart(2, '0');
        return {
          id,
          title: t(`features.${id}.title`),
          body: t(`features.${id}.body`),
          phoneAlt: t(`features.${id}.phoneAlt`),
          summary: t(`features.summary.${id}`),
          screen,
        };
      }),
    [t],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <div className="landing">
      <header className={`topnav${scrolled ? ' is-scrolled' : ''}`}>
        <a className="brand" href="#top">
          <img className="brand-mark" src="/logo-192.png" alt="" width={32} height={32} decoding="async" />
          <span className="brand-name">{BRAND}</span>
        </a>
        <nav className="topnav-links" aria-label={t('nav.primary')}>
          <a href="#about">{t('nav.about')}</a>
          <a href="#features">{t('nav.features')}</a>
          <a href="#stats">{t('nav.stats')}</a>
          <a href="#faq">{t('nav.faq')}</a>
          <a href="#owners">{t('nav.owners')}</a>
        </nav>
        <div className="topnav-actions">
          <LanguageSwitcher />
          <a className="btn btn-nav-cta" href="#download">
            {t('nav.downloadCta')}
          </a>
          <button
            type="button"
            className="menu-toggle"
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </header>

      {menuOpen ? (
        <div className="mobile-menu" role="dialog" aria-modal="true" aria-label={t('nav.primary')}>
          <a href="#about" onClick={() => setMenuOpen(false)}>
            {t('nav.about')}
          </a>
          <a href="#features" onClick={() => setMenuOpen(false)}>
            {t('nav.features')}
          </a>
          <a href="#stats" onClick={() => setMenuOpen(false)}>
            {t('nav.stats')}
          </a>
          <a href="#faq" onClick={() => setMenuOpen(false)}>
            {t('nav.faq')}
          </a>
          <a href="#owners" onClick={() => setMenuOpen(false)}>
            {t('nav.owners')}
          </a>
          <a className="btn btn-gold" href="#download" onClick={() => setMenuOpen(false)}>
            {t('nav.downloadCta')}
          </a>
        </div>
      ) : null}

      <main id="top">
        <section className="hero">
          <div className="hero-media" aria-hidden>
            <img src={heroImg} alt="" />
            <div className="hero-shade" />
            <div className="hero-grain" />
          </div>
          <div className="hero-inner">
            <div className="hero-copy hero-enter">
              <p className="brand-lockup">{BRAND}</p>
              <Heading as="h1" text={t('hero.headline')} />
              <Body text={t('hero.sub')} className="hero-sub" />
              <StoreBadges t={t} />
              <p className="age-note">{t('hero.ageNote')}</p>
            </div>
            <div className="hero-phone hero-enter hero-enter-delay">
              <PhoneFrame variant="home" alt={t('hero.phoneAlt')} />
            </div>
          </div>
        </section>

        <section className="about" id="about">
          <Reveal className="about-inner">
            <Heading as="h2" text={t('about.title')} />
            <Body text={t('about.body')} />
          </Reveal>
        </section>

        <section className="features" id="features">
          <Reveal className="section-head">
            <p className="eyebrow">{t('features.eyebrow')}</p>
            <Heading as="h2" text={t('features.title')} />
          </Reveal>

          <Reveal className="feature-summary">
            {features.map((f) => (
              <a key={f.id} className="feature-summary-card" href={`#feature-${f.id}`}>
                <span className="feature-summary-num">#{f.id}</span>
                <span>{f.summary}</span>
              </a>
            ))}
          </Reveal>

          {features.map((feature, index) => (
            <Reveal
              as="article"
              key={feature.id}
              className={`feature-row${index % 2 === 1 ? ' alt' : ''}`}
            >
              <div className="feature-copy" id={`feature-${feature.id}`}>
                <span className="feature-num">#{feature.id}</span>
                <Heading as="h3" text={feature.title} />
                <Body text={feature.body} />
              </div>
              <div className="feature-visual">
                <div className="feature-stage">
                  <PhoneFrame variant={feature.screen} alt={feature.phoneAlt} />
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal as="article" className="pickup">
            <div className="pickup-copy">
              <span className="pickup-tag">{t('pickup.tag')}</span>
              <Heading as="h3" text={t('pickup.title')} />
              <Body text={t('pickup.body')} />
            </div>
            <div className="pickup-visual">
              <img src={pickupImg} alt={t('pickup.imgAlt')} />
              <div className="pickup-phone">
                <PhoneFrame variant="feed" alt={t('features.04.phoneAlt')} />
              </div>
            </div>
          </Reveal>
        </section>

        <section className="stats" id="stats">
          <Reveal className="section-head">
            <p className="eyebrow">{t('stats.eyebrow')}</p>
            <Heading as="h2" text={t('stats.title')} />
          </Reveal>
          <Reveal className="stats-grid">
            <div className="stats-item">
              <p className="stats-value">{t('stats.shopsValue')}</p>
              <p className="stats-label">{t('stats.shops')}</p>
            </div>
            <div className="stats-item">
              <p className="stats-value">{t('stats.areasValue')}</p>
              <p className="stats-label">{t('stats.areas')}</p>
            </div>
            <div className="stats-item">
              <p className="stats-value">{t('stats.usersValue')}</p>
              <p className="stats-label">{t('stats.users')}</p>
            </div>
          </Reveal>
          <p className="stats-note">{t('stats.note')}</p>
        </section>

        <section className="safety" id="safety">
          <Reveal className="section-head">
            <p className="eyebrow">{t('safety.eyebrow')}</p>
            <Heading as="h2" text={t('safety.title')} />
            <Body text={t('safety.body')} />
          </Reveal>
          <Reveal className="safety-grid">
            {(['1', '2', '3'] as const).map((n) => (
              <div key={n} className="safety-card">
                <Heading as="h3" text={t(`safety.${n}.title`)} />
                <Body text={t(`safety.${n}.body`)} />
              </div>
            ))}
          </Reveal>
        </section>

        <section className="faq" id="faq">
          <Reveal className="section-head">
            <p className="eyebrow">{t('faq.eyebrow')}</p>
            <Heading as="h2" text={t('faq.title')} />
          </Reveal>
          <Reveal className="faq-list">
            {(['1', '2', '3', '4'] as const).map((n) => (
              <FaqItem key={n} q={t(`faq.${n}.q`)} a={t(`faq.${n}.a`)} />
            ))}
          </Reveal>
        </section>

        <section className="owners" id="owners">
          <Reveal className="owners-inner">
            <p className="eyebrow light">{t('owners.eyebrow')}</p>
            <Heading as="h2" text={t('owners.title')} />
            <Body text={t('owners.body')} />
            <div className="owners-actions">
              <a className="btn btn-teal" href={`${OWNER_URL}/register`}>
                {t('owners.cta')}
                <span aria-hidden>→</span>
              </a>
              <a className="owners-secondary" href={`${OWNER_URL}/login`}>
                {t('owners.signIn')}
              </a>
            </div>
          </Reveal>
        </section>

        <section className="final-cta" id="download">
          <div className="final-media" aria-hidden>
            <img src={ctaImg} alt="" />
            <div className="final-shade" />
            <div className="hero-grain" />
          </div>
          <Reveal className="final-inner">
            <Heading as="h2" text={t('cta.title')} />
            <Body text={t('cta.sub')} />
            <StoreBadges t={t} />
            <p className="age-note light">{t('cta.ageNote')}</p>
          </Reveal>
        </section>
      </main>

      <footer className="footer" id="footer">
        <div className="footer-brand">
          <img className="brand-mark" src="/logo-192.png" alt="" width={32} height={32} decoding="async" />
          <span className="brand-name">{BRAND}</span>
        </div>
        <a className="btn btn-outline" href={`mailto:${CONTACT_EMAIL}`}>
          {t('footer.inquiry')}
        </a>
        <div className="social" aria-label={t('footer.social')}>
          <a href="#footer" aria-label={t('footer.facebook')}>
            f
          </a>
          <a href="#footer" aria-label={t('footer.instagram')}>
            ig
          </a>
          <a href="#footer" aria-label={t('footer.x')}>
            𝕏
          </a>
        </div>
        <div className="footer-links">
          <a href="#about">{t('footer.about')}</a>
          <a href="#owners">{t('footer.owners')}</a>
          <a href={legalPath(locale, 'help')}>{t('footer.help')}</a>
          <a href={legalPath(locale, 'privacy')}>{t('footer.privacy')}</a>
          <a href={legalPath(locale, 'terms')}>{t('footer.terms')}</a>
          <a href="#footer">{t('footer.company')}</a>
          <a href="#footer">{t('footer.tokusho')}</a>
          <a href={`mailto:${CONTACT_EMAIL}`}>{t('links.contact')}</a>
        </div>
        <p className="footer-age">{t('footer.age')}</p>
        <p className="copyright">
          © {new Date().getFullYear()} {BRAND}
        </p>
      </footer>
    </div>
  );
}
